import { GD } from "src/GD";
import { refreshBalanceSources } from "./refreshBalanceSources";
import { getIssuerRequestContext, isIssuerRequestContextCurrent } from "./issuerRequestContext";
import { requestListPage } from "./listPage";

type CardTemplate = Partial<ICardRaw> & Record<string, unknown>;

const withoutIssuer = <T extends object>(data: T): T => {
	const payload = { ...data } as T & { issuer?: unknown };
	delete payload.issuer;
	return payload;
};

// Groups the server-driven fields into the order a buyer actually decides them.
// Fields the backend adds later land in "More" instead of silently disappearing.
const SECTIONS: { title: string; order: string[]; columns: number; wide?: string[] }[] = [
	{ title: "Choose your card", order: ["network", "type", "currency", "bin_visa", "bin_mastercard"], columns: 3, wide: ["bin_visa", "bin_mastercard"] },
	{ title: "Name and first top-up", order: ["name", "amount"], columns: 2 },
	{ title: "Spending limits", order: ["limit_daily", "limit_monthly"], columns: 2 },
	{ title: "Auto top-up", order: ["balance_threshold", "balance_topup_target"], columns: 2 },
	{ title: "Merchant restrictions", order: ["allowed_descriptors", "suspicious_freeze_threshold"], columns: 2, wide: ["allowed_descriptors"] }
]

const FIELD_COPY: Record<string, { label?: string; details?: string }> = {
	network: { label: "Network", details: "" },
	type: { details: "" },
	currency: { details: "" },
	name: { label: "Card name" },
	amount: { label: "Initial load", details: "Moved from your balance to the card right away." },
	limit_daily: { label: "Daily limit", details: "0 means no limit." },
	limit_monthly: { label: "Monthly limit", details: "0 means no limit." },
	balance_threshold: { label: "Top up when balance falls below", details: "0 turns auto top-up off." },
	balance_topup_target: { label: "Amount to add each time", details: "Leave empty to add just enough to reach the threshold." },
	allowed_descriptors: { label: "Allowed merchants" },
	suspicious_freeze_threshold: { label: "Freeze after this many off-list payments", details: "" }
}

const setMeta = (data: Record<string, unknown>, field: string, key: string, value: string) => {
	const meta = data[`$__${field}`]
	if (meta && typeof meta === "object") (meta as Record<string, unknown>)[key] = value
	data[`$__${field}_${key}`] = value
}

const applyCardFormLayout = (data: Record<string, unknown>) => {
	for (const [field, copy] of Object.entries(FIELD_COPY)) {
		if (!(field in data)) continue
		if (copy.label !== undefined) setMeta(data, field, "label", copy.label)
		if (copy.details !== undefined) setMeta(data, field, "details", copy.details)
	}
	const listed = new Set(SECTIONS.flatMap(section => section.order))
	const rest = Object.keys(data).filter(key => !key.startsWith("$") && !listed.has(key))
	data.$__decorator = {
		blocks: [...SECTIONS, { title: "More", order: rest, columns: 1 }].map(({ title, order, columns, wide = [] }) => ({
			title,
			order,
			sx: {
				display: "grid",
				gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
				gap: "16px",
				alignItems: "start",
				...Object.fromEntries(wide.map(field => [`& > [data-form-field='${field}']`, { gridColumn: "1 / -1" }])),
				"@media (max-width: 620px)": { gridTemplateColumns: "minmax(0, 1fr)" }
			}
		}))
	}
}

export class CardsService {
	constructor() {
		GD.REQ_CARDS.listener = this.getCards;
		GD.REQ_CARDS_PAGE.listener = query => requestListPage<ICardSaved, ICardListSummary>("cards/list", query, this.normalizeCard);
		GD.REQ_GET_CARD.listener = this.getCard;
		GD.REQ_GET_CARD_TEMPLATE.listener = this.getCardTemplate;
		GD.REQ_CREATE_CARD.listener = this.createCard;
		GD.REQ_UPDATE_CARD.listener = this.updateCard;
		GD.REQ_CARD_NAME_GET.listener = data => this.getEditModel("name", data);
		GD.REQ_CARD_LIMITS_GET.listener = data => this.getEditModel("limits", data);
		GD.REQ_CARD_NAME_SET.listener = data => this.saveEditModel("name", { uid: data.uid, name: data.name });
		GD.REQ_CARD_LIMITS_SET.listener = data => this.saveEditModel("limits", {
			uid: data.uid,
			limit_daily: data.limit_daily,
			limit_monthly: data.limit_monthly,
			balance_threshold: data.balance_threshold,
			...(data.balance_topup_target === undefined ? {} : { balance_topup_target: data.balance_topup_target }),
		});
		GD.REQ_CARD_TX_CONTROL.listener = this.updateTxControl;
		GD.REQ_FREEZE_CARD.listener = this.freezeCard;
		GD.REQ_UNFREEZE_CARD.listener = this.unfreezeCard;
		GD.REQ_CARD_TOPUP_TEMPLATE.listener = this.getCardTopUpTemplate;
		GD.REQ_CARD_TOPUP.listener = this.topUpCard;
		GD.REQ_CARD_TOPUPS.listener = this.getCardTopUps;
		GD.REQ_CARD_TOPUP_GET.listener = this.getCardTopUp;
		GD.REQ_CARD_WITHDRAW.listener = this.withdrawCard;
		GD.REQ_CARD_WITHDRAW_LIST.listener = this.getCardWithdrawals;
		GD.REQ_CARD_WITHDRAW_GET.listener = this.getCardWithdrawal;
		GD.REQ_CARD_3DS_LIST.listener = this.getCard3dsCodes;
		GD.REQ_CARD_3DS_REFRESH.listener = this.refreshCard3dsCodes;
		GD.REQ_BLOCK_CARD.listener = this.blockCard;
		GD.REQ_BLOCK_CARD_PREVIEW.listener = this.getBlockCardPreview;
		GD.REQ_BLOCK_CARD_STATUS.listener = this.getBlockCardStatus;
		GD.REQ_CARD_TRANSACTIONS.listener = this.getCardTransactions;

		GD.REQ_COMMON_CARD_CURRENCIES.listener = this.getCardCurrencies;
		GD.REQ_COMMON_CARD_SCHEMES.listener = this.getCardSchemes;
		GD.REQ_COMMON_CARD_TYPES.listener = this.getCardTypes;
		GD.REQ_COMMON_CITIES.listener = this.getCities;
	}

	private getCities: (data: { countryCode: string; issuer: number }) => Promise<IAPIResponse<ICity[]>> = async data => {
		if (!Number.isInteger(data?.issuer) || data.issuer <= 0) {
			return { error: { code: 400, message: "Issuer is required" } };
		}
		return (await GD.REQ_API.request({
			httpMethod: "POST",
			method: "common/cities",
			data: { countryCode: data.countryCode ?? "", issuer: data.issuer },
		})) as IAPIResponse<ICity[]>;
	};

	private getCardCurrencies: (context: { card_holder_uid?: string } | undefined) => Promise<IAPIResponse<{ code: string }[]>> = async context => {
		return (await GD.REQ_API.request({ httpMethod: "POST", method: "common/cardCurrencies", data: context })) as IAPIResponse<{ code: string }[]>;
	};

	private getCardSchemes: (context: { card_holder_uid?: string } | undefined) => Promise<IAPIResponse<{ code: number; name: string }[]>> = async context => {
		return (await GD.REQ_API.request({ httpMethod: "POST", method: "common/cardSchemes", data: context })) as IAPIResponse<{
			code: number;
			name: string;
		}[]>;
	};

	private getCardTypes: (context: { card_holder_uid?: string } | undefined) => Promise<IAPIResponse<{ code: number; name: string }[]>> = async context => {
		return (await GD.REQ_API.request({ httpMethod: "POST", method: "common/cardTypes", data: context })) as IAPIResponse<{
			code: number;
			name: string;
		}[]>;
	};

	private normalizeCard = (card: ICardSaved): ICardSaved => {
		if (card?.providerData?.balance === undefined || card.providerData.balance === null) return card;

		return {
			...card,
			balance: card.providerData.balance,
		};
	};

	private refreshCards = async () => {
		const requestContext = getIssuerRequestContext();
		const cardsResponse = await this.getCards({ force: true });
		if (cardsResponse.data && isIssuerRequestContextCurrent(requestContext)) {
			GD.S_CARDS_LIST_UPDATED.invoke(cardsResponse.data);
		}
	};

	private processTemplate = (data: unknown): ICardRaw => {
		const template: CardTemplate = {
			...(data !== null && typeof data === "object" ? data as Record<string, unknown> : {}),
		};
		const numericFields = ["amount", "limit_daily", "limit_monthly", "balance_threshold", "balance_topup_target"];
		const binsInfo: IBinInfo[] = Array.isArray(template.bins_info) ? template.bins_info : [];
		const decorateBinOptions = (values: unknown): unknown => {
			if (!Array.isArray(values)) return values;
			return values.map(value => {
				const bin = typeof value === "object" && value !== null && "value" in value
					? String((value as { value: unknown }).value)
					: String(value);
				const requiresKyc = binsInfo.some(info => String(info.bin) === bin && info.need_kyc);
				return {
					name: bin,
					value: bin,
					...(requiresKyc ? { badge: "Identity check" } : {}),
				};
			});
		};

		// Wasabi: the BIN whitelist is delivered as the `bin_visa` / `bin_mastercard`
		// select fields (their options come on `$__bin_visa_values` /
		// `$__bin_mastercard_values`), plus a `bins_info` array used only for the
		// fee/price preview. Hide the raw `bins_info` array and `issuance_fee` so
		// the form renderer does not turn them into stray input fields.
		template.$__bins_info_hidden = true;
		template.$__issuance_fee_hidden = true;
		template.$__issuer_hidden = true;
		// `cards/get` can include values from a saved/provider card even when it is
		// used as the creation template. They are server-managed and must never be
		// rendered as editable fields (especially PAN/CVV/expiry in providerData).
		template.$__balance_hidden = true;
		template.$__withdraw_residual_cents_hidden = true;
		template.$__providerData_hidden = true;
		for (const field of numericFields) {
			if (field in template) template[`$__${field}_type`] = "number";
		}
		// Keep the descriptors field visually aligned with the other card inputs
		// while retaining textarea semantics for pasted descriptor lists.
		template.$__allowed_descriptors_sx = {
			...(template.$__allowed_descriptors_sx ?? {}),
			minHeight: "auto",
		};
		template.$__bin_visa_values = decorateBinOptions(template.$__bin_visa_values);
		template.$__bin_mastercard_values = decorateBinOptions(template.$__bin_mastercard_values);

		// Inject the context
		// if (context?.card_holder_uid) {
		// 	template.card_holder_uid = context.card_holder_uid;
		// 	template.$__card_holder_uid_hidden = true;
		// } else {
		// 	template.$__card_holder_uid_hidden = false;
		// 	if (!template.$__card_holder_uid_label) template.$__card_holder_uid_label = "Card Holder";

		// 	if (cardHolders && cardHolders.length > 0) {
		// 		template.$__card_holder_uid_type = "select";
		// 		template.$__card_holder_uid_values = cardHolders.map(ch => ({
		// 			name: `${ch.first_name} ${ch.last_name} (${ch.email})`,
		// 			value: ch.uid,
		// 		}));
		// 	} else if (Array.isArray(template.$__card_holder_uid_values)) {
		// 		template.$__card_holder_uid_type = "select";
		// 	}
		// }

		// if (schemes && schemes.length > 0) {
		// 	template.$__scheme_type = "select";
		// 	template.$__scheme_values = schemes.map(s => ({ name: s.name, value: String(s.code) }));
		// } else if (Array.isArray(template.$__scheme_values)) {
		// 	template.$__scheme_type = "select";
		// }

		// if (types && types.length > 0) {
		// 	template.$__type_type = "select";
		// 	template.$__type_values = types.map(t => ({ name: t.name, value: String(t.code) }));
		// } else if (Array.isArray(template.$__type_values)) {
		// 	template.$__type_type = "select";
		// }

		// if (currencies && currencies.length > 0) {
		// 	template.$__currency_type = "select";
		// 	template.$__currency_values = currencies.map((c: any) => ({ name: c.code, value: c.code }));
		// } else if (Array.isArray(template.$__currency_values)) {
		// 	template.$__currency_type = "select";
		// }

		// if (Array.isArray(template.$__bin_values)) {
		// 	template.$__bin_type = "select";
		// }

		applyCardFormLayout(template);

		return template as unknown as ICardRaw;
	};

	private getCards: (options: void | { force?: boolean; issuer?: number }) => Promise<IAPIResponse<ICardSaved[]>> = async options => {
		const res = await GD.REQ_API.request({
			httpMethod: "GET",
			method: "cards/list",
			params: options?.issuer === undefined ? undefined : { issuer: options.issuer },
		});

		if (res.data && !res.error) {
			return { data: (res.data as unknown as ICardSaved[]).map(this.normalizeCard) };
		}

		return res as IAPIResponse<ICardSaved[]>;
	};

	private getCard: (data: { uid: string; card_holder_uid: string }) => Promise<IAPIResponse<ICardSaved>> = async data => {
		if (!data?.uid) {
			return { data: {} as ICardSaved };
		}

		const res = await GD.REQ_API.request({
			httpMethod: "GET",
			method: `cards/get?uid=${data.uid}&card_holder_uid=${data.card_holder_uid}`,
		});
		if (res.data) {
			// const decorated = await this.decorate(res.data);
			return { data: this.normalizeCard(res.data as unknown as ICardSaved) };
		}
		return res as IAPIResponse<ICardSaved>;
	};

	private getCardTemplate: (context?: { card_holder_uid?: string; issuer?: number }) => Promise<IAPIResponse<ICardRaw>> = async context => {
		const res = await GD.REQ_API.request({
			httpMethod: "POST",
			method: "cards/get",
			data: {
				uid: "-1",
				...(context?.card_holder_uid ? { card_holder_uid: context.card_holder_uid } : {}),
				...(context?.issuer === undefined ? {} : { issuer: context.issuer }),
			},
		});

		// Fetch dependencies
		// let cardHolders: ICardHolderSaved[] = [];
		// let currencies: { code: string }[] = [];
		// let schemes: { code: number; name: string }[] = [];
		// let types: { code: number; name: string }[] = [];

		try {
			// const results = await Promise.all([
			// 	GD.REQ_CARDHOLDERS.request(),
			// 	this.getCardCurrencies(context),
			// 	this.getCardSchemes(context),
			// 	this.getCardTypes(context),
			// ]);
			// if (results[0].data) cardHolders = results[0].data;
			// if (results[1].data) currencies = results[1].data;
			// if (results[2].data) schemes = results[2].data;
			// if (results[3].data) types = results[3].data;
		} catch (e) {
			console.warn("Failed to fetch dependencies for template", e);
		}

		if (res.data) {
			const decorated = this.processTemplate(res.data);
			return { data: decorated };
		}
		return res as IAPIResponse<ICardRaw>;
	};

	private getEditModel = async (form: "name" | "limits", data: { uid: string }): Promise<IAPIResponse<ICardEditModel>> =>
		await GD.REQ_API.request({ method: `cards/${form}/get`, httpMethod: "POST", data }) as IAPIResponse<ICardEditModel>;

	private saveEditModel = async (form: "name" | "limits", data: ICardNameUpdate | ICardLimitsUpdate): Promise<IAPIResponse<IStatusResponse>> => {
		const res = await GD.REQ_API.request({ method: `cards/${form}/set`, httpMethod: "POST", data }) as IAPIResponse<IStatusResponse>;
		if (res.error) return res;
		if (!res.data?.status) return { error: { code: -1, message: "Unknown error" } };
		await this.refreshCards();
		return res;
	};

	private updateCard: (data: ICardUpdate) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const res = (await GD.REQ_API.request({
			method: "cards/update",
			httpMethod: "POST",
			data,
		})) as IAPIResponse<IStatusResponse>;

		if (res.error) return res;

		if (res.data?.status === true) {
			await this.refreshCards();
			return { data: res.data };
		}
		return { error: { code: -1, message: "Unknown error" } };
	};

	private updateTxControl: (data: ICardTxControl) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const res = (await GD.REQ_API.request({
			method: "cards/tx-control",
			httpMethod: "POST",
			data,
		})) as IAPIResponse<IStatusResponse>;

		if (res.error) return res;

		const result = (res.data as IStatusResponse & { data?: IStatusResponse })?.data ?? res.data;
		if (result?.status === true) {
			await this.refreshCards();
			return { data: result };
		}

		return { error: { code: -1, message: "Unknown error" } };
	};

	private freezeCard: (data: { card_uid: string }) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const res = (await GD.REQ_API.request({
			method: "cards/freeze",
			httpMethod: "POST",
			data,
		})) as IAPIResponse<IStatusResponse>;

		if (res.error) return res;

		if (res.data?.status === true) {
			await this.refreshCards();
			return { data: res.data };
		}

		return { error: { code: -1, message: "Unknown error" } };
	};

	private unfreezeCard: (data: { card_uid: string }) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const res = (await GD.REQ_API.request({
			method: "cards/unfreeze",
			httpMethod: "POST",
			data,
		})) as IAPIResponse<IStatusResponse>;

		if (res.error) return res;

		if (res.data?.status === true) {
			await this.refreshCards();
			return { data: res.data };
		}

		return { error: { code: -1, message: "Unknown error" } };
	};

	private createCard: (data: ICardSaved) => Promise<IAPIResponse<ICardRaw>> = async data => {
		const res = await GD.REQ_API.request({
			method: "cards/create",
			httpMethod: "POST",
			// The backend derives the issuer from BIN. A hidden template field must
			// never leak into the create contract.
			data: withoutIssuer(data),
		});

		if (res.data && !res.error) {
			const [cards] = await Promise.all([
				this.getCards(),
				refreshBalanceSources(),
			]);
			if (cards.data) GD.S_CARDS_LIST_UPDATED.invoke(cards.data);
		}

		return res as IAPIResponse<ICardRaw>;
	};

	private topUpCard: (data: ICardTopUp) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const res = (await GD.REQ_API.request({
			method: "cards/topup",
			httpMethod: "POST",
			// The card itself is the issuer boundary for money operations.
			data: withoutIssuer(data),
		})) as IAPIResponse<IStatusResponse>;

		if (res.error) return { error: res.error };

		if (res.data?.status === true) {
			await Promise.all([this.refreshCards(), refreshBalanceSources()]);
			return { data: res.data };
		}

		return { error: { code: -1, message: "Unknown error" } };
	};

	private getCardTopUpTemplate: () => Promise<IAPIResponse<ICardTopUp>> = async () => {
		const res = await GD.REQ_API.request({
			method: "cards/topup/get?uid=-1",
			httpMethod: "GET",
		});

		return res as IAPIResponse<ICardTopUp>;
	};

	private getCardTopUps: (data: { card_uid: string }) => Promise<IAPIResponse<ICardTopUpSaved[]>> = async data => {
		if (!data) return { data: [] };
		const res = await GD.REQ_API.request({
			method: "cards/topup/list",
			httpMethod: "POST",
			data,
		});
		return res as IAPIResponse<ICardTopUpSaved[]>;
	};

	private getCardTopUp: (data: { id: string | number }) => Promise<IAPIResponse<ICardTopUpSaved>> = async data => {
		if (!data) return { data: {} as ICardTopUpSaved };

		const res = await GD.REQ_API.request({
			method: "cards/topup/get",
			httpMethod: "POST",
			data,
		});
		return res as IAPIResponse<ICardTopUpSaved>;
	};

	private withdrawCard: (data: ICardWithdrawal) => Promise<IAPIResponse<IStatusResponse>> = async data => {
		const res = (await GD.REQ_API.request({
			method: "cards/withdraw",
			httpMethod: "POST",
			data: withoutIssuer(data),
		})) as IAPIResponse<IStatusResponse>;

		if (res.error) return { error: res.error };

		if (res.data?.status === true) {
			await Promise.all([
				this.refreshCards(),
				refreshBalanceSources(),
			]);
			return { data: res.data };
		}
		return { error: { code: -1, message: "Unknown error" } };
	};

	private getCardWithdrawals: (data: { card_uid: string }) => Promise<IAPIResponse<ICardWithdrawalSaved[]>> = async data => {
		if (!data) return { data: [] };
		const res = await GD.REQ_API.request({
			method: "cards/withdraw/list",
			httpMethod: "POST",
			data,
		});
		return res as IAPIResponse<ICardWithdrawalSaved[]>;
	};

	private getCardWithdrawal: (data: { id: number | string }) => Promise<IAPIResponse<ICardWithdrawalSaved>> = async data => {
		if (!data) return { data: {} as ICardWithdrawalSaved };
		const res = await GD.REQ_API.request({
			method: "cards/withdraw/get",
			httpMethod: "POST",
			data,
		});
		return res as IAPIResponse<ICardWithdrawalSaved>;
	};

	private getCard3dsCodes: (data: ICard3dsListRequest) => Promise<IAPIResponse<ICard3dsCode[]>> = async data => {
		if (!data?.card_uid) return { data: [] };
		return (await GD.REQ_API.request({
			method: "cards/3ds/list",
			httpMethod: "POST",
			data,
		})) as IAPIResponse<ICard3dsCode[]>;
	};

	private refreshCard3dsCodes: (data: ICard3dsRefreshRequest) => Promise<IAPIResponse<ICard3dsRefreshResponse>> = async data => {
		return (await GD.REQ_API.request({
			method: "cards/3ds/refresh",
			httpMethod: "POST",
			data,
		})) as IAPIResponse<ICard3dsRefreshResponse>;
	};

	private getCardTransactions: (data: { uid: string; card_holder_uid: string }) => Promise<IAPIResponse<ICardTransaction[]>> = async data => {
		if (!data) return { data: [] };

		const res = await GD.REQ_API.request({
			method: "companies/transactions/all",
			httpMethod: "POST",
			data: { card_uid: data.uid, no_limit: true },
		});
		if (res.error) return { error: res.error };

		const transactions = (res.data as Partial<ICompanyTransactionsResponse> | undefined)?.transactions;
		if (Array.isArray(transactions)) {
			const list: ICardTransaction[] = transactions.map(t => ({
				id: String(t.id),
				date: t.created_at,
				description: t.description || "Transaction",
				amount: Number(t.amount || 0),
				currency: t.currency,
				status: t.status,
				direction: t.direction,
				operation_id: t.operation_id ?? undefined,
				type_id: t.type_id,
				balance: t.balance,
				account_id: t.account_id,
				counterparty_account_id: t.counterparty_account_id,
				created_at: t.created_at,
				merchant: t.merchant,
			}));
			return { data: list };
		}

		return { data: [] };
	};

	private blockCard: (data: { card_uid: string; type?: "buyer" | "company" }) => Promise<IAPIResponse<ICardBlockResponse>> = async data => {
		const res = (await GD.REQ_API.request({
			method: "cards/block",
			httpMethod: "POST",
			data,
		})) as IAPIResponse<ICardBlockResponse>;

		if (res.error) return res;

		if (res.data?.operation_uid) {
			return { data: res.data };
		}
		return { error: { code: -1, message: "Unknown error" } };
	};

	private getBlockCardPreview: (data: { card_uid: string; type?: "buyer" | "company" }) => Promise<IAPIResponse<ICardBlockPreview>> = async data => {
		return (await GD.REQ_API.request({
			method: "cards/block/preview",
			httpMethod: "POST",
			data
		})) as IAPIResponse<ICardBlockPreview>
	};

	private getBlockCardStatus: (data: { operation_uid: string }) => Promise<IAPIResponse<ICardBlockStatus>> = async data => {
		if (!data?.operation_uid) {
			return { error: { code: 1, message: "Operation UID is required" } };
		}

		const res = (await GD.REQ_API.request({
			method: "cards/block/status",
			httpMethod: "POST",
			data,
		})) as IAPIResponse<ICardBlockStatus>;

		if (res.data?.status === "completed") {
			// Auxiliary balance/list failures must not hide confirmed completion.
			void Promise.allSettled([
				this.refreshCards(),
				refreshBalanceSources(),
			]);
		}

		return res;
	};
}
